import React, { useState } from 'react';
import { Award, MapPin, Building, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [showAllCities, setShowAllCities] = useState(false);

  // Comprehensive DFW Cities by County
  const citiesByCounty = {
    "Dallas County": [
      "Dallas", "Irving", "Garland", "Grand Prairie", "Mesquite", "Carrollton", 
      "Richardson", "Rowlett", "DeSoto", "Cedar Hill", "Lancaster", "Duncanville", 
      "Farmers Branch", "Balch Springs", "Seagoville", "Sachse", "Sunnyvale", 
      "Highland Park", "University Park", "Cockrell Hill", "Addison", "Coppell"
    ],
    "Tarrant County": [
      "Fort Worth", "Arlington", "North Richland Hills", "Euless", "Bedford", 
      "Grapevine", "Hurst", "Keller", "Southlake", "Colleyville", "Mansfield", 
      "Haltom City", "Watauga", "Benbrook", "Richland Hills", "Forest Hill", 
      "White Settlement", "Saginaw", "Lake Worth", "Crowley", "Azle", "Kennedale",
      "River Oaks", "Sansom Park", "Westworth Village", "Everman", "Lakeside"
    ],
    "Collin County": [
      "Plano", "McKinney", "Frisco", "Allen", "Wylie", "Murphy", "Sachse", 
      "Lucas", "Prosper", "Celina", "Anna", "Princeton", "Melissa", "Fairview", 
      "Parker", "Lavon", "Nevada", "Farmersville", "Blue Ridge", "Lowry Crossing",
      "New Hope", "Weston", "Westminster"
    ],
    "Denton County": [
      "Denton", "Lewisville", "Flower Mound", "Little Elm", "The Colony", 
      "Corinth", "Highland Village", "Lake Dallas", "Sanger", "Aubrey", 
      "Pilot Point", "Argyle", "Roanoke", "Trophy Club", "Copper Canyon", 
      "Double Oak", "Bartonville", "Northlake", "Justin", "Ponder", "Krum",
      "Shady Shores", "Hickory Creek", "Oak Point", "Cross Roads", "Krugerville"
    ],
    "Rockwall County": [
      "Rockwall", "Heath", "Royse City", "Fate", "McLendon-Chisholm", "Mobile City"
    ],
    "Kaufman County": [
      "Kaufman", "Terrell", "Forney", "Crandall", "Kemp", "Mabank", "Combine", 
      "Oak Grove", "Scurry", "Elmo", "Talty", "Post Oak Bend City", "Grays Prairie"
    ],
    "Ellis County": [
      "Waxahachie", "Midlothian", "Ennis", "Red Oak", "Glenn Heights", "Italy", 
      "Maypearl", "Milford", "Ferris", "Palmer", "Bardwell", "Oak Leaf", "Alma"
    ],
    "Johnson County": [
      "Cleburne", "Burleson", "Joshua", "Alvarado", "Keene", "Grandview", 
      "Godley", "Venus", "Rio Vista", "Crowley", "Lillian", "Cross Timber"
    ],
    "Parker County": [
      "Weatherford", "Willow Park", "Hudson Oaks", "Aledo", "Springtown", 
      "Azle", "Annetta", "Cool", "Millsap", "Peaster", "Reno", "Sanctuary"
    ],
    "Wise County": [
      "Decatur", "Rhome", "Newark", "Bridgeport", "Boyd", "Aurora", "Paradise", 
      "Alvord", "Chico", "New Fairview", "Lake Bridgeport", "Runaway Bay"
    ],
    "Hood County": [
      "Granbury", "Tolar", "Lipan", "Cresson", "Pecan Plantation", "DeCordova"
    ],
    "Hunt County": [
      "Greenville", "Commerce", "Quinlan", "Caddo Mills", "Wolfe City", 
      "Campbell", "Celeste", "Lone Oak", "West Tawakoni", "Hawk Cove"
    ]
  };

  // Flatten for display - popular cities first
  const popularCities = [
    "Dallas", "Fort Worth", "Plano", "Frisco", "McKinney", "Arlington", 
    "Irving", "Garland", "Richardson", "Denton", "Lewisville", "Allen",
    "Flower Mound", "Carrollton", "Grapevine", "Southlake", "Keller",
    "Mesquite", "Rockwall", "Mansfield", "Grand Prairie", "Euless",
    "Bedford", "Coppell", "The Colony", "Wylie", "Rowlett", "DeSoto"
  ];

  // All cities flattened
  const allCities = Object.values(citiesByCounty).flat();
  const uniqueCities = [...new Set(allCities)].sort();

  // Cities to show (popular or all)
  const displayCities = showAllCities ? uniqueCities : popularCities;

  const counties = [
    { name: "Dallas County", slug: "dallas-county" },
    { name: "Tarrant County", slug: "tarrant-county" },
    { name: "Collin County", slug: "collin-county" },
    { name: "Denton County", slug: "denton-county" },
    { name: "Rockwall County", slug: "rockwall-county" },
    { name: "Kaufman County", slug: "kaufman-county" },
    { name: "Ellis County", slug: "ellis-county" },
    { name: "Johnson County", slug: "johnson-county" },
    { name: "Parker County", slug: "parker-county" },
    { name: "Wise County", slug: "wise-county" },
    { name: "Hood County", slug: "hood-county" },
    { name: "Hunt County", slug: "hunt-county" },
  ];

  // Convert city name to slug
  const cityToSlug = (city) => city.toLowerCase().replace(/\s+/g, '-');

  return (
    <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-10 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* DFW Cities Section */}
        <div className="mb-8 pb-8 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-orange-500" />
            FSBO Homes For Sale By Owner in DFW
            <span className="ml-2 text-sm font-normal text-gray-400">({uniqueCities.length} cities)</span>
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {displayCities.map((city) => (
              <Link
                key={city}
                to={`/homes-for-sale/${cityToSlug(city)}`}
                className="text-xs text-gray-400 hover:text-orange-400 transition-colors"
              >
                {city}
              </Link>
            ))}
          </div>
          {!showAllCities && uniqueCities.length > popularCities.length && (
            <button
              onClick={() => setShowAllCities(true)}
              className="mt-3 text-xs text-orange-400 hover:text-orange-300 flex items-center"
            >
              <ChevronDown className="w-4 h-4 mr-1" />
              Show all {uniqueCities.length} DFW cities
            </button>
          )}
          {showAllCities && (
            <button
              onClick={() => setShowAllCities(false)}
              className="mt-3 text-xs text-orange-400 hover:text-orange-300 flex items-center"
            >
              <ChevronUp className="w-4 h-4 mr-1" />
              Show fewer cities
            </button>
          )}
        </div>

        {/* Counties Section */}
        <div className="mb-8 pb-8 border-b border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2 text-orange-500" />
            FSBO Homes By County
          </h3>
          <div className="flex flex-wrap gap-2">
            {counties.map((county) => (
              <Link
                key={county.slug}
                to={`/county/${county.slug}`}
                className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-orange-400 px-3 py-1.5 rounded-full transition-colors border border-gray-700"
              >
                {county.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          {/* Left side - Copyright */}
          <div className="text-center md:text-left">
            <p className="text-sm text-gray-300">
              © {new Date().getFullYear()} Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup>. All rights reserved.
            </p>
            <div className="inline-flex items-center bg-orange-600 text-white px-2 py-1 rounded-full text-xs font-bold mt-2">
              <Award className="w-3 h-3 mr-1" />
              Since 1998 • 27 Years Strong
            </div>
            <p className="text-xs text-gray-400 mt-2">
              America&apos;s Original FSBO Marketplace
            </p>
            <p className="text-xs text-gray-500 mt-1 italic">
              Not affiliated with any real estate brokerage firm
            </p>
          </div>

          {/* Center - Quick Links */}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/seller-resources" className="text-gray-300 hover:text-white transition-colors">
              Seller Resources
            </Link>
            <Link to="/buyer-resources" className="text-gray-300 hover:text-white transition-colors">
              Buyer Resources
            </Link>
            <Link to="/home-valuation" className="text-gray-300 hover:text-white transition-colors">
              Free Valuation
            </Link>
            <Link to="/blog" className="text-gray-300 hover:text-white transition-colors">
              Blog
            </Link>
            <Link to="/press-releases" className="text-gray-300 hover:text-white transition-colors">
              Press
            </Link>
            <Link to="/history" className="text-gray-300 hover:text-white transition-colors">
              Our Story
            </Link>
            <Link to="/terms" className="text-gray-300 hover:text-white transition-colors">
              Terms
            </Link>
            <Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors">
              Privacy
            </Link>
            <Link to="/sms-terms" className="text-gray-300 hover:text-white transition-colors">
              SMS Terms
            </Link>
          </div>

          {/* Right side - Credit */}
          <div className="text-center md:text-right">
            <p className="text-sm text-gray-300">
              Website by{' '}
              <a 
                href="https://Web-techservices.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-300 transition-colors font-semibold"
              >
                Web-Techservices AI
              </a>
            </p>
          </div>
        </div>

        {/* SEO Text */}
        <div className="mt-8 pt-6 border-t border-gray-700 text-center">
          <p className="text-xs text-gray-500 max-w-4xl mx-auto">
            Its-Sold.com™ is the Dallas-Fort Worth area's premier For Sale By Owner (FSBO) real estate marketplace. 
            List your home for FREE and save thousands on agent commissions. Browse homes for sale by owner in Dallas, 
            Fort Worth, Plano, Frisco, McKinney, Arlington, Irving, Garland, Richardson, Denton, Lewisville, Allen,
            Flower Mound, Carrollton, Grapevine, Southlake, Keller, Rockwall, Mansfield, and 150+ more DFW cities across
            Dallas, Tarrant, Collin, Denton, Rockwall, Kaufman, Ellis, Johnson, Parker, Wise, Hood, and Hunt counties.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
